import { useEffect, useState, useRef } from "react";
import * as signalR from '@microsoft/signalr'
import { useToaster } from '../context/ToasterContext';

const { HubConnectionBuilder, LogLevel } = signalR;

/** Avoid `//` when base ends with `/` and path starts with `/` (e.g. negotiate URL). */
function buildSignalRHubUrl(baseUrl, hubPath) {
  const base = String(baseUrl ?? '')
    .trim()
    .replace(/\/+$/, '');
  const path = String(hubPath ?? '')
    .trim()
    .replace(/^\/+/, '');
  if (!base) return `/${path}`;
  return `${base}/${path}`;
}

const SIGNALR_HUB_URL = buildSignalRHubUrl(import.meta.env.VITE_API_BASE_URL, 'notificationHub');

export function useNotificationHub(accessToken, onNotificationReceived = null) {
  const [connection, setConnection] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [messages, setMessages] = useState([]);
  const connectionRef = useRef(null);
  const { showSuccess, showInfo, showWarning, showError } = useToaster();

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    // Prevent creating multiple connections
    if (connectionRef.current) {
      return;
    }

    const newConnection = new HubConnectionBuilder()
      .withUrl(SIGNALR_HUB_URL, {
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling,
        withCredentials: false,
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(LogLevel.Information) // Changed from Debug to Information to reduce console noise
      .build()

    setConnection(newConnection);
    connectionRef.current = newConnection;

    // Connection state handlers
    newConnection.onclose((error) => {
      setIsConnected(false);
      if (error) {
        setConnectionError(`Connection closed: ${error.message}`);
      } else {
        setConnectionError('Connection closed');
      }
    });

    newConnection.onreconnecting((error) => {
      setIsConnected(false);
      setConnectionError('Reconnecting...');
    });

    newConnection.onreconnected((connectionId) => {
      setIsConnected(true);
      setConnectionError(null);
    });

    // Cleanup function
    return () => {
      if (newConnection && newConnection.state === signalR.HubConnectionState.Connected) {
        newConnection.stop();
      }
    };
  }, [accessToken]);

  useEffect(() => {
    if (!connection) return;

    // Check if connection is already started or starting
    if (connection.state === signalR.HubConnectionState.Connected ||
      connection.state === signalR.HubConnectionState.Connecting) {
      return;
    }

    const startConnection = async () => {
      try {
        await connection.start();

        setIsConnected(true);
        setConnectionError(null);

        // Join the user-specific notification group
        try {
          await connection.invoke("JoinNotificationGroup");
        } catch (err) {
          // Join group error handled silently
        }
      } catch (err) {
        setConnectionError(`Connection failed: ${err.message}`);
        setIsConnected(false);
      }
    };

    startConnection();

    // Handle messages from server
    connection.on("JoinedNotificationGroup", (msg) => {
      // Joined notification group event received
    });

    connection.on("Error", (msg) => {
      setConnectionError(msg);
    });

    // Handle actual notification events
    connection.on("ReceiveNotification", (notification) => {
      // Show toast notification
      if (typeof notification === 'string') {
        showInfo('New Notification', notification);
      } else if (notification && notification.title) {
        showInfo(notification.title, notification.body || notification.message || '');
      } else if (notification && notification.message) {
        showInfo('New Notification', notification.message);
      } else {
        showInfo('New Notification', JSON.stringify(notification));
      }

      setMessages(prev => [...prev, notification]);

      // Call the callback to refresh notification list (if provided)
      if (onNotificationReceived) {
        onNotificationReceived(notification);
      }
    });

    return () => {
      // Remove event listeners
      connection.off("JoinedNotificationGroup");
      connection.off("Error");
      connection.off("ReceiveNotification");

      // Stop connection if connected
      if (connection.state === signalR.HubConnectionState.Connected) {
        connection.stop().catch(err => {
          // Error stopping connection handled silently
        });
      }
    };
  }, [connection]);


  // Function to manually refresh notifications
  const refreshNotifications = () => {
    // Refresh notifications
  };

  // Function to get connection status
  const getConnectionStatus = () => {
    if (!connection) return 'No connection';

    return {
      state: connection.state,
      connectionId: connection.connectionId,
      isConnected: isConnected,
      error: connectionError,
      messageCount: messages.length
    };
  };

  // Function to manually reconnect
  const reconnect = async () => {
    if (connection) {
      try {
        await connection.stop();
      } catch (error) {
        // Error stopping connection handled silently
      }
    }

    // Clear connection state
    setConnection(null);
    setIsConnected(false);
    setConnectionError(null);

    // Restart connection
    setTimeout(() => {
      if (accessToken) {
        // Restarting SignalR connection
      }
    }, 1000);
  };

  return {
    connection,
    isConnected,
    connectionError,
    messages,
    refreshNotifications,
    getConnectionStatus,
    reconnect,

  };
}
